import React from 'react';
import LaunchIcon from '@mui/icons-material/Launch';

import { ServiceTypeEnum } from 'utils/enums';
import { TIMEZONE_LABELS, TIMEZONE_OFFSETS } from 'utils/timezones';
import { formatToSpecificTimezone } from 'utils/dates';

const mapLocation = (facility) =>
  !facility?.address?.streetAddress || !facility?.address?.locality
    ? null
    : `https://www.google.com/maps/search/?api=1&query=${facility.address.streetAddress},${facility.address.locality}`;

const phoneNumberUrl = (phoneNumber) => phoneNumber && `tel:${phoneNumber}`;

const urlField = ({ value, label, url }) => ({
  value,
  label,
  url,
  blankUrl: true,
});

const mapField = (facility) =>
  urlField({
    label: 'Map',
    value: <LaunchIcon />,
    url: mapLocation(facility),
  });

const locationField = ({ address }) => ({
  label: 'Location',
  value: `${address?.locality ? address?.locality.concat(', ') : ''} ${
    address?.region || ''
  }`,
});

const timezoneField = ({ timezone }) => ({
  label: 'Timezone',
  value:
    timezone &&
    `${TIMEZONE_LABELS[timezone]} - ${formatToSpecificTimezone(
      new Date(),
      'HH:mm',
      TIMEZONE_OFFSETS[timezone],
    )}`,
});

const phoneField = ({ number, label }) => ({
  label,
  value: number,
  url: phoneNumberUrl(number),
});

const neuroFacilityInfoMap = (facility) => [
  mapField(facility),
  locationField(facility),
  timezoneField(facility),
  phoneField({ number: facility.edNumber, label: 'ED Number' }),
  phoneField({ number: facility.itPhoneNumber, label: 'IT Number' }),
  phoneField({
    number: facility.callCenterPhoneNumber,
    label: 'Call Center Number',
  }),
  phoneField({
    number: facility.primaryFaxNumber,
    label: 'Primary Fax Number',
  }),
  phoneField({
    number: facility.secondaryFaxNumber,
    label: 'Secondary Fax Number',
  }),
  urlField({ label: 'EMR', value: facility.emrName, url: facility.emrLink }),
  urlField({
    label: 'Radiology',
    value: facility.radiology,
    url: facility.radiologyLink,
  }),
  { label: 'Mobile Imaging', value: facility.mobileImagings?.join(', ') },
  urlField({
    label: 'Camera',
    value: facility.camera,
    url: facility.cameraLink,
  }),
  { label: 'Thrombolytic', value: facility.thrombolytic },
  { label: 'LVO Protocol', value: facility.lvoProtocol ?? '' },
  { label: 'Direct Beam-In', value: facility.directBeamIn ? 'Yes' : 'No' },
  { label: 'CTP', value: facility.isCpt ? 'Yes' : 'No' },
  {
    label: 'Ext Window Protocol',
    value: facility.isExtWindowProtocol ? 'Yes' : 'No',
  },
  urlField({
    label: 'Shared Sign-Out',
    value: facility.sharedSignout,
    url: facility.sharedSignoutLink,
  }),
  urlField({
    label: 'TeleStroke Hub',
    value: '',
    url: facility.strokeHub,
  }),
  urlField({
    label: 'TeleNH hub',
    value: '',
    url: facility.neuroHospitalHub,
  }),
  urlField({
    label: 'EEG/Peds EEG Hub',
    value: '',
    url: facility.eegPedsEegHub,
  }),
];

const teleNeuroFacilityInfoMap = (facility) => [
  mapField(facility),
  locationField(facility),
  timezoneField(facility),
  phoneField({ number: facility.edNumber, label: 'ED Number' }),
  phoneField({ number: facility.itPhoneNumber, label: 'IT Number' }),
  phoneField({
    number: facility.callCenterPhoneNumber,
    label: 'Call Center Number',
  }),
  phoneField({
    number: facility.primaryFaxNumber,
    label: 'Primary Fax Number',
  }),
  phoneField({
    number: facility.secondaryFaxNumber,
    label: 'Secondary Fax Number',
  }),
  urlField({ label: 'EMR', value: facility.emrName, url: facility.emrLink }),
  urlField({
    label: 'Radiology',
    value: facility.radiology,
    url: facility.radiologyLink,
  }),
  { label: 'Mobile Imaging', value: facility.mobileImagings?.join(', ') },
  urlField({
    label: 'Camera',
    value: facility.camera,
    url: facility.cameraLink,
  }),
  urlField({
    label: 'Shared Sign-Out',
    value: facility.sharedSignout,
    url: facility.sharedSignoutLink,
  }),
  urlField({
    label: 'TeleStroke Hub',
    value: '',
    url: facility.strokeHub,
  }),
  urlField({
    label: 'TeleNH hub',
    value: '',
    url: facility.neuroHospitalHub,
  }),
  urlField({
    label: 'EEG/Peds EEG Hub',
    value: '',
    url: facility.eegPedsEegHub,
  }),
];

const eegFacilityInfoMap = (facility) => [
  mapField(facility),
  locationField(facility),
  timezoneField(facility),
  phoneField({
    number: facility.hospitalMainNumber,
    label: 'Hospital Main Number',
  }),
  phoneField({ number: facility.edNumber, label: 'ED Number' }),
  phoneField({ number: facility.itPhoneNumber, label: 'IT Number' }),
  phoneField({
    number: facility.primaryFaxNumber,
    label: 'Primary Fax Number',
  }),
  phoneField({
    number: facility.secondaryFaxNumber,
    label: 'Secondary Fax Number',
  }),
  urlField({ label: 'EMR', value: facility.emrName, url: facility.emrLink }),
  urlField({
    label: 'EEG Software Link',
    value: facility.eegSoftware,
    url: facility.eegSoftwareLink,
  }),
];

// eslint-disable-next-line import/prefer-default-export
export const facilityInfoMap = (facility, consultType) => {
  switch (consultType) {
    case ServiceTypeEnum.NEURO:
      return neuroFacilityInfoMap(facility);
    case ServiceTypeEnum.TELE_NEURO:
      return teleNeuroFacilityInfoMap(facility);
    case ServiceTypeEnum.EEG:
    case ServiceTypeEnum.PEDS_EEG:
      return eegFacilityInfoMap(facility);
    default:
      return null;
  }
};
